import { Directive, ElementRef, OnDestroy, NgZone, inject, input, effect, computed, signal } from '@angular/core';
import { CssHelperService } from '@app/core/services/css-helper/css-helper.service';
import { createThrottle } from '@app/shared/utils/throttle-controller';

export type ElementSizeConfig = Record<string, number>;

@Directive({
  selector: '[gcElementWidthClass]',
  standalone: true,
  exportAs: 'gcElementWidthClass',
})
export class ElementWidthClassDirective implements OnDestroy {
  private cssHelper = inject(CssHelperService);
  private ngZone = inject(NgZone);

  /**
   * @description Configuration object where keys are class names and value is element width (px). Example:
   * {
   *   'is-big': 480,
   *   'is-medium': 320,
   * }
   */
  public gcElementWidthClass = input<ElementSizeConfig | undefined>();

  public gcElementWidthClassIsActive = input<boolean>(true);

  private defaultConfig: ElementSizeConfig = {
    'is-xsm': parseFloat(this.cssHelper.readCssVariable('--breakpoint-xsm')), // 360px;
    'is-sm': parseFloat(this.cssHelper.readCssVariable('--breakpoint-sm')), // 576px;
    'is-md': parseFloat(this.cssHelper.readCssVariable('--breakpoint-md')), // 768px;
    'is-lg': parseFloat(this.cssHelper.readCssVariable('--breakpoint-lg')), // 992px;
    'is-xl': parseFloat(this.cssHelper.readCssVariable('--breakpoint-xl')), // 1200px;
    'is-xxl': parseFloat(this.cssHelper.readCssVariable('--breakpoint-2xl')), // 1400px;
  };

  private config = computed(() =>
    Object.entries(this.gcElementWidthClass() || this.defaultConfig)
      .map(([className, width]) => ({ className, width }))
      .sort((a, b) => b.width - a.width)
  );

  private observer: ResizeObserver | null = null;
  private throttle = createThrottle(400);

  /**
   *
   * @description Method to check if the class is currently applied to the element. Can be used in template for conditional styling or logic based on the applied classes.
   * @example
   * <div [gcElementWidthClass]="{ 'is-big': 480, 'is-medium': 320 }" #widthClass="gcElementWidthClass">
   *  @if (widthClass.activeClasses().include('is-big')) {
   *    <span>Big view</span>
   *  }
   * </div>
   */
  public activeClasses = signal<string[]>([]);
  public activeClassesEmpty = computed<boolean>(() => this.activeClasses().length === 0);

  constructor(private el: ElementRef) {
    effect(() => {
      const active = this.gcElementWidthClassIsActive();

      this.config();

      if (active) {
        this.startObserving();
      } else {
        this.stopObserving();
        this.clearAllClasses();
      }
    });
  }

  private startObserving(): void {
    if (this.observer) return;

    this.ngZone.runOutsideAngular(() => {
      this.observer = new ResizeObserver(entries => {
        const width = entries[0].contentRect.width;

        this.throttle.run(() => this.updateClasses(width));
      });

      this.observer.observe(this.el.nativeElement);
    });
  }

  private stopObserving(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  private updateClasses(width: number): void {
    const config = this.config();

    const matchClasses = config.filter(a => width >= a.width).map(a => a.className);

    this.ngZone.run(() => {
      if (!matchClasses.length) {
        this.clearAllClasses();
        return;
      }

      this.clearAllClasses();

      const activeClasses = this.activeClasses();

      if (activeClasses.length) {
        this.el.nativeElement.classList.remove(...activeClasses);
      }

      this.el.nativeElement.classList.add(...matchClasses);
      this.activeClasses.set(matchClasses);
    });
  }

  private clearAllClasses(): void {
    const activeClasses = this.activeClasses();
    if (activeClasses.length) {
      this.el.nativeElement.classList.remove(...activeClasses);
      this.activeClasses.set([]);
    }
  }

  ngOnDestroy(): void {
    this.stopObserving();

    this.throttle.cancel();
  }
}
